/**
 * ParentContactsCard — pluggable section for ProfilePage + nudge banner.
 *
 * Reasoning: kid-safety is the platform's core promise. We need a way to reach
 * a parent if a student writes something distressing in chat. We keep this:
 * - Optional (the user explicitly chose "nudge with a yellow banner")
 * - Transparent (purpose statement is shown on the form)
 * - Easy to fill (mother + optional secondary)
 */
import { useEffect, useState } from 'react';
import { apiGet, apiPut } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Phone, ShieldCheck, Lock, Save } from 'lucide-react';
import { toast } from 'sonner';

const RELATIONS = ['mother', 'father', 'guardian'];

export default function ParentContactsCard() {
  const [data, setData] = useState({
    primary_phone: '', primary_relationship: 'mother', primary_name: '',
    secondary_phone: '', secondary_relationship: 'father', secondary_name: '',
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet('/profile/parent-contacts')
      .then(r => {
        const pc = r?.parent_contacts || {};
        setData(d => ({
          ...d,
          primary_phone: pc.primary_phone || '',
          primary_relationship: pc.primary_relationship || 'mother',
          primary_name: pc.primary_name || '',
          secondary_phone: pc.secondary_phone || '',
          secondary_relationship: pc.secondary_relationship || 'father',
          secondary_name: pc.secondary_name || '',
        }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      await apiPut('/profile/parent-contacts', data);
      toast.success('Saved. Thank you — this stays private to our care team.');
      // Notify any listeners (Dashboard banner) so the nudge can vanish.
      window.dispatchEvent(new CustomEvent('parent-contacts-updated'));
    } catch (e) {
      toast.error((e?.message || '').replace(/^HTTP \d+:\s*/, '') || 'Could not save');
    } finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <Card className="rounded-3xl border-2 border-emerald-200 overflow-hidden" data-testid="parent-contacts-card">
      <div className="bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold text-slate-900">For your safety — parent contact</h3>
            <p className="text-sm text-slate-700 font-body mt-1 leading-relaxed">
              We keep one or two parent phone numbers <strong>only</strong> so we can reach a parent if we
              ever need to — for anything about your account or learning. Never used for marketing, never
              shared with other students or schools.
            </p>
            <p className="text-[11px] text-slate-500 font-body mt-2 inline-flex items-center gap-1">
              <Lock className="w-3 h-3" /> Visible only to GurukulVeda admins & the support team
            </p>
          </div>
        </div>

        {/* Primary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="text-[11px] font-heading uppercase tracking-wider text-slate-500 mb-1 block">Relationship</label>
            <select
              data-testid="primary-relationship"
              value={data.primary_relationship}
              onChange={e => setData(d => ({ ...d, primary_relationship: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-body bg-white capitalize">
              {RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-heading uppercase tracking-wider text-slate-500 mb-1 block">Name (optional)</label>
            <Input data-testid="primary-name" value={data.primary_name}
              onChange={e => setData(d => ({ ...d, primary_name: e.target.value }))}
              placeholder="e.g. Priya Sharma" className="rounded-xl text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-heading uppercase tracking-wider text-slate-500 mb-1 block">Phone <span className="text-rose-500">*</span></label>
            <Input data-testid="primary-phone" type="tel" value={data.primary_phone}
              onChange={e => setData(d => ({ ...d, primary_phone: e.target.value }))}
              placeholder="+91 98765 43210" className="rounded-xl text-sm font-mono" />
          </div>
        </div>

        {/* Secondary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="text-[11px] font-heading uppercase tracking-wider text-slate-500 mb-1 block">Backup relationship</label>
            <select
              data-testid="secondary-relationship"
              value={data.secondary_relationship}
              onChange={e => setData(d => ({ ...d, secondary_relationship: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-body bg-white capitalize">
              {RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-heading uppercase tracking-wider text-slate-500 mb-1 block">Name (optional)</label>
            <Input data-testid="secondary-name" value={data.secondary_name}
              onChange={e => setData(d => ({ ...d, secondary_name: e.target.value }))}
              placeholder="e.g. Rajesh Sharma" className="rounded-xl text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-heading uppercase tracking-wider text-slate-500 mb-1 block">Phone (optional)</label>
            <Input data-testid="secondary-phone" type="tel" value={data.secondary_phone}
              onChange={e => setData(d => ({ ...d, secondary_phone: e.target.value }))}
              placeholder="+91 98765 12345" className="rounded-xl text-sm font-mono" />
          </div>
        </div>

        <div className="flex justify-end">
          <Button data-testid="save-parent-contacts" onClick={onSave} disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save contacts'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

/** Yellow nudge banner for the Dashboard — prompts students to add a parent number. */
export function ParentContactsNudge() {
  const [needs, setNeeds] = useState(null); // null=loading, true=needs, false=already on file
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('parent-nudge-dismissed') === '1'; } catch { return false; }
  });

  const refresh = async () => {
    try {
      const r = await apiGet('/profile/parent-contacts');
      const pc = r?.parent_contacts || {};
      setNeeds(!(pc.primary_phone || pc.secondary_phone));
    } catch { setNeeds(false); }
  };

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener('parent-contacts-updated', handler);
    return () => window.removeEventListener('parent-contacts-updated', handler);
  }, []);

  if (needs !== true || dismissed) return null;

  return (
    <Card data-testid="parent-nudge"
      className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-3 sm:p-4 flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4">
      <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="font-heading text-[13px] sm:text-sm font-semibold text-amber-900">A friendly reminder</p>
        <p className="text-xs sm:text-sm text-slate-700 font-body mt-0.5 leading-relaxed">
          Could you add a parent's phone number in <strong>Profile → "Parent contact"</strong>? We'd just like to
          have it on file in case we ever need to reach a parent about your account. We'd rather have it and never need it.
        </p>
        <div className="flex flex-wrap gap-2 mt-2">
          <a href="/profile" data-testid="parent-nudge-cta"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-heading">
            Add a number now
          </a>
          <button data-testid="parent-nudge-dismiss"
            onClick={() => { try { sessionStorage.setItem('parent-nudge-dismissed', '1'); } catch (_) {} setDismissed(true); }}
            className="px-3 py-1.5 rounded-full text-xs font-heading text-amber-700 hover:bg-amber-100">
            Maybe later
          </button>
        </div>
      </div>
    </Card>
  );
}
